import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  addSpaceTeamMember,
  addSpaceUserMember,
  getSpaceMembersOptions,
  getSpaceMembersQueryKey,
  listSpaceConnectorsOptions,
  removeSpaceTeamMember,
  removeSpaceUserMember,
  updateSpaceTeamMember,
  updateSpaceUserMember,
} from "@/api";
import { type MembershipApi, MembershipPanel } from "@/components/build/MembershipPanel";
import { InlineNotice } from "@/components/InlineNotice";
import { Page } from "@/components/Page";
import { Text } from "@/components/Text";
import { useCurrentUser } from "@/features/account/useCurrentUser";
import { formatList } from "@/i18n/format";
import { useRefreshSpace, useSettingsSpace } from "./useSpaceSettings";

/** People and teams in the space, via Build's MembershipPanel (admins manage; editors read). */
export function MembersSection() {
  const { t } = useTranslation("spaces", { keyPrefix: "settings" });
  const space = useSettingsSpace();
  const me = useCurrentUser();
  const refresh = useRefreshSpace(space.uuid);
  // Per-person connectors here: new members need their own token (MHS-823).
  const needToken = useQuery({
    ...listSpaceConnectorsOptions({ path: { space_uuid: space.uuid } }),
    enabled: !space.isPersonal,
    select: (r) =>
      (r.data.connectors ?? []).filter((b) => b.connector.authMode === "per_user").map((b) => b.connector.name),
  });

  const api = useMemo<MembershipApi>(() => {
    const path = { space_uuid: space.uuid };
    // Every change also refreshes the space page (member avatars, counts).
    const then = async <T,>(p: Promise<T>): Promise<T> => {
      const result = await p;
      await refresh();
      return result;
    };
    return {
      membersOptions: () => getSpaceMembersOptions({ path }),
      membersQueryKey: () => getSpaceMembersQueryKey({ path }),
      addUser: (id, role) => then(addSpaceUserMember({ path, body: { userUuid: id, role }, throwOnError: true })),
      updateUserRole: (id, role) =>
        then(updateSpaceUserMember({ path: { ...path, user_uuid: id }, body: { role }, throwOnError: true })),
      removeUser: (id) => then(removeSpaceUserMember({ path: { ...path, user_uuid: id }, throwOnError: true })),
      addTeam: (id, role) => then(addSpaceTeamMember({ path, body: { teamUuid: id, role }, throwOnError: true })),
      updateTeamRole: (id, role) =>
        then(updateSpaceTeamMember({ path: { ...path, team_uuid: id }, body: { role }, throwOnError: true })),
      removeTeam: (id) => then(removeSpaceTeamMember({ path: { ...path, team_uuid: id }, throwOnError: true })),
    };
  }, [space.uuid, refresh]);

  return (
    <Page title={t("nav.members")}>
      {space.isPersonal ? (
        <Text tone="secondary">{t("members.privateNote")}</Text>
      ) : (
        <>
          {(needToken.data ?? []).length > 0 && (
            <InlineNotice tone="info">
              {t("members.connectorTokens", { names: formatList(needToken.data ?? []) })}
            </InlineNotice>
          )}
          <MembershipPanel api={api} currentUserUuid={me.data?.user.uuid} />
        </>
      )}
    </Page>
  );
}
